import { useState } from "react";

import { collectExecutableCodeBlocks } from "@corca/tiptap";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { FormButton } from "@/components/form/FormButton";
import { FormToggle } from "@/components/form/FormToggle";
import { twMerge } from "@/utils/twMerge";

import type { DocumentModel } from "@/models/DocumentModel";

export const CodeExecutor = observer(function CodeExecutor({
  doc,
  isCollapsed,
}: {
  doc: DocumentModel;
  isCollapsed?: boolean;
}) {
  const [includeFollowing, setIncludeFollowing] = useState(true);
  const t = useTranslations("sidebar.blocks.code");
  const { tiptap, editorState } = doc.editor;

  const handleOnToggleScope = () => {
    setIncludeFollowing((i) => !i);
  };

  const { cellId, language, cellGroup } = editorState.codeBlockAttrs ?? {};

  if (!cellId || !language) return null;

  const cellStatus = doc.marimo.cellStatuses.get(cellId);
  const isRemovedByChangeRequest = tiptap
    ? collectExecutableCodeBlocks(tiptap.state.doc).removed.some(
        (node) => node.attrs.cellId === cellId,
      )
    : false;

  const disabled = isRemovedByChangeRequest || !doc.marimo.isCellAvailable(cellId);
  const isBusy = cellStatus === "running" || cellStatus === "queued";

  const handleExecute = () => {
    if (!tiptap || !language) return;

    const group = collectExecutableCodeBlocks(tiptap.state.doc).active.filter(
      (n) => n.attrs.cellGroup === cellGroup,
    );

    const index = group.findIndex((n) => n.attrs.cellId === cellId);
    if (index === -1) return;

    // current and following cells
    let cellsToExecute = group.slice(index);

    if (!includeFollowing) {
      cellsToExecute = cellsToExecute.slice(0, 1);
    }

    doc.marimo.runCells(cellsToExecute.map((c) => c.attrs.cellId as string));
  };

  return (
    <div className="flex flex-col">
      <FormButton
        isBusy={isBusy}
        disabled={disabled}
        variant="accent"
        size="sm"
        className={twMerge("h-8", isCollapsed && "h-7 w-7 p-0 lowercase text-[10px]")}
        onClick={handleExecute}
      >
        {t("run")}
      </FormButton>
      {!isCollapsed && (
        <label
          htmlFor="execution-scope"
          className="flex items-center justify-between cursor-pointer text-xs tracking-tight p-1.25"
        >
          {t("executionScopeLabel")}
          <FormToggle
            id="execution-scope"
            checked={includeFollowing}
            className="mt-1"
            onClick={(e) => e.stopPropagation()}
            onChange={handleOnToggleScope}
            asInput
          />
        </label>
      )}
    </div>
  );
});
